import React from 'react';
import { type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import { BodyText, Container, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationSelectedIngredientKindSection = (
  props: CustomizationSelectedIngredientKindSectionProps,
) => {
  const { ingredientKindName, formattedQuantity, helperText, style, children } = props;

  // ─── a11y Selectors ────────────────────────────────────────────────────────

  const { props: a11yProps } = useA11ySelector('customization.aside-selected-ingredients.section', {
    group: ingredientKindName.toLowerCase(),
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Container style={[styles.container, style]} {...a11yProps}>
      <View style={styles.label}>
        <View style={styles.labelTextContainer}>
          <BodyText style={styles.ingredientKindName} sizeMatch={['16']} bold>
            {ingredientKindName}
          </BodyText>

          {helperText ? (
            <BodyText style={styles.helperText} sizeMatch={['14']}>
              {helperText}
            </BodyText>
          ) : null}
        </View>

        {formattedQuantity ? (
          <BodyText style={styles.quantityLabel} sizeMatch={['16']}>
            {formattedQuantity}
          </BodyText>
        ) : null}
      </View>

      {children}
    </Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: theme.spacing['6'],
    padding: theme.spacing['4'],
    borderBottomWidth: 1,
    borderColor: theme.colors.GRAY,
    rowGap: theme.spacing['2'],
  },
  label: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing['2'],
  },
  labelTextContainer: {
    flex: 1,
  },
  quantityLabel: {
    color: theme.colors.CHARCOAL,
  },
  ingredientKindName: {
    textTransform: 'capitalize',
  },
  helperText: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationSelectedIngredientKindSectionProps = {
  ingredientKindName: string;
  formattedQuantity: string | undefined;
  helperText?: string | null;
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
};
